import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { authApi, effectiveUsageProfile, type UsageProfile } from './auth-api';

export const ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * The signed-in session (`GET /api/auth/me`). The protected layout ensures it before
 * rendering; `retry: false` because a 401 is an answer (already retried once after a
 * refresh by the HTTP client), not a hiccup.
 */
export const meQueryOptions = queryOptions({
  queryKey: ME_QUERY_KEY,
  queryFn: ({ signal }) => authApi.me(signal),
  retry: false,
  staleTime: 60_000,
});

export function useMe() {
  return useQuery(meQueryOptions);
}

/**
 * What this session's screens show (spec 044): the organization's usage profile narrowed by
 * the member's area, `both` until the session loads — the profile only hides what is not
 * used, so the safe default shows everything.
 */
export function useUsageProfile(): UsageProfile {
  const me = useMe();
  if (!me.data) return 'both';
  return effectiveUsageProfile(me.data.organization.usageProfile, me.data.membership.area);
}

/** Signs out: the API revokes the session and clears its cookies; the cache goes too. */
export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => authApi.logout(),
    onSettled: async () => {
      queryClient.clear();
      await navigate({ to: '/login' });
    },
  });
}
